/**
 * Video Settings Component - Glass Morphism Design
 * Configuration panel for video generation settings
 * Validates: Requirements 17.2, 17.3
 */
import { useVideoStore, VideoModelInfo } from '../../stores/videoStore';
import { Icons } from '../icons';
import { THEME } from '../../styles/theme';

interface VideoSettingsProps {
  disabled?: boolean;
}

export function VideoSettings({ disabled }: VideoSettingsProps) {
  const {
    providers,
    selectedProvider,
    selectedModel,
    settings,
    setSettings,
  } = useVideoStore();

  const currentProvider = providers.find(p => p.id === selectedProvider);
  const currentModel = currentProvider?.models.find(m => m.id === selectedModel);

  const aspectRatios = ['16:9', '9:16', '1:1', '4:3', '3:4'] as const;
  const resolutions = ['720p', '1080p', '4k'] as const;
  const fpsOptions = [24, 30, 60];

  // Get duration options based on model limits
  const getDurationOptions = (model: VideoModelInfo | undefined) => {
    if (!model) return [5, 10, 15, 30];
    const { min, max } = model.durationLimits;
    const options = [5, 10, 15, 30, 60].filter(d => d >= min && d <= max);
    return options;
  };

  const durationOptions = getDurationOptions(currentModel);

  return (
    <div
      className="rounded-2xl p-5 border relative overflow-hidden"
      style={{
        backgroundColor: 'rgba(255,255,255,0.03)',
        borderColor: THEME.border,
      }}
    >
      {/* Dot pattern overlay */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage: 'radial-gradient(rgba(255,255,255,0.03) 1px, transparent 1px)',
          backgroundSize: '12px 12px',
        }}
      />

      <div className="relative">
        <div className="flex items-center gap-2 mb-4">
          <Icons.Settings size={16} style={{ color: THEME.accentColor }} />
          <h3 className="text-sm font-semibold uppercase tracking-wider" style={{ color: THEME.textMain }}>
            Video Settings
          </h3>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {/* Aspect Ratio */}
          <div>
            <label className="block text-xs mb-2 font-medium" style={{ color: THEME.textSecondary }}>
              Aspect Ratio
            </label>
            <select
              value={settings.aspectRatio || '16:9'}
              onChange={(e) => setSettings({ aspectRatio: e.target.value as typeof aspectRatios[number] })}
              disabled={disabled}
              className="w-full text-sm rounded-lg px-3 py-2 border transition-all duration-200 focus:outline-none disabled:opacity-50"
              style={{
                backgroundColor: THEME.glassLight,
                backdropFilter: 'blur(10px)',
                borderColor: THEME.border,
                color: THEME.textMain,
              }}
            >
              {aspectRatios.map(ratio => (
                <option key={ratio} value={ratio}>{ratio}</option>
              ))}
            </select>
          </div>

          {/* Duration */}
          <div>
            <label className="block text-xs mb-2 font-medium" style={{ color: THEME.textSecondary }}>
              Duration (seconds)
            </label>
            <select
              value={settings.duration || 10}
              onChange={(e) => setSettings({ duration: parseInt(e.target.value) })}
              disabled={disabled}
              className="w-full text-sm rounded-lg px-3 py-2 border transition-all duration-200 focus:outline-none disabled:opacity-50"
              style={{
                backgroundColor: THEME.glassLight,
                backdropFilter: 'blur(10px)',
                borderColor: THEME.border,
                color: THEME.textMain,
              }}
            >
              {durationOptions.map(duration => (
                <option key={duration} value={duration}>{duration}s</option>
              ))}
            </select>
          </div>

          {/* Resolution */}
          <div>
            <label className="block text-xs mb-2 font-medium" style={{ color: THEME.textSecondary }}>
              Resolution
            </label>
            <select
              value={settings.resolution || '1080p'}
              onChange={(e) => setSettings({ resolution: e.target.value as typeof resolutions[number] })}
              disabled={disabled}
              className="w-full text-sm rounded-lg px-3 py-2 border transition-all duration-200 focus:outline-none disabled:opacity-50"
              style={{
                backgroundColor: THEME.glassLight,
                backdropFilter: 'blur(10px)',
                borderColor: THEME.border,
                color: THEME.textMain,
              }}
            >
              {resolutions.map(res => (
                <option key={res} value={res}>{res}</option>
              ))}
            </select>
          </div>

          {/* FPS */}
          <div>
            <label className="block text-xs mb-2 font-medium" style={{ color: THEME.textSecondary }}>
              Frame Rate
            </label>
            <select
              value={settings.fps || 30}
              onChange={(e) => setSettings({ fps: parseInt(e.target.value) })}
              disabled={disabled}
              className="w-full text-sm rounded-lg px-3 py-2 border transition-all duration-200 focus:outline-none disabled:opacity-50"
              style={{
                backgroundColor: THEME.glassLight,
                backdropFilter: 'blur(10px)',
                borderColor: THEME.border,
                color: THEME.textMain,
              }}
            >
              {fpsOptions.map(fps => (
                <option key={fps} value={fps}>{fps} fps</option>
              ))}
            </select>
          </div>
        </div>

        {/* Model Features */}
        {currentModel && currentModel.features.length > 0 && (
          <div className="pt-4 mt-4" style={{ borderTop: `1px solid ${THEME.border}` }}>
            <p className="text-xs mb-2 font-medium" style={{ color: THEME.textMuted }}>
              Model Features:
            </p>
            <div className="flex flex-wrap gap-2">
              {currentModel.features.map((feature, index) => (
                <span
                  key={index}
                  className="text-xs px-3 py-1 rounded-lg"
                  style={{
                    backgroundColor: 'rgba(255,255,255,0.05)',
                    color: THEME.textSecondary,
                  }}
                >
                  {feature}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
